import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

interface Partner {
  id: string;
  name: string;
  slug: string;
  contact_email: string;
  logo_url: string;
  status: 'active' | 'inactive';
  access_password?: string;
  needs_password_reset?: boolean;
  billing_details: {
    tax_id?: string;
    address?: string;
    postal_code?: string;
    city?: string;
    country?: string;
    legal_name?: string;
    po_number?: string;
  };
}

interface Service {
  id: string;
  partner_id: string;
  name: string;
  price: number;
}

export default function AdminPartners() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  
  // Services Management State
  const [selectedPartnerForServices, setSelectedPartnerForServices] = useState<Partner | null>(null);
  const [partnerServices, setPartnerServices] = useState<Service[]>([]);
  const [masterServices, setMasterServices] = useState<{name: string, price: number}[]>([]);
  const [newService, setNewService] = useState({ name: '', price: '' });
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    contact_email: '',
    logo_url: '',
    access_password: '',
    needs_password_reset: false,
    billing_details: {
      tax_id: '',
      address: '',
      postal_code: '',
      city: '',
      country: 'España',
      legal_name: '',
      po_number: ''
    }
  });

  useEffect(() => {
    fetchPartners();
    fetchMasterServices();
  }, []);

  const fetchPartners = async () => {
    setLoading(true);
    const { data } = await supabase.from('partners').select('*').order('created_at', { ascending: false });
    if (data) setPartners(data);
    setLoading(false);
  };

  const fetchMasterServices = async () => {
    const { data } = await supabase.from('partner_services').select('name, price');
    if (data) {
      const unique = data.reduce((acc: any, curr) => {
        if (!acc[curr.name]) acc[curr.name] = curr.price;
        return acc;
      }, {});
      setMasterServices(Object.keys(unique).map(name => ({ name, price: unique[name] })));
    }
  };

  const fetchServices = async (partnerId: string) => {
    const { data } = await supabase.from('partner_services').select('*').eq('partner_id', partnerId).order('name', { ascending: true });
    if (data) setPartnerServices(data);
  };

  const handleAddService = async (serviceName?: string, servicePrice?: number) => {
    if (!selectedPartnerForServices) return;
    const name = serviceName || newService.name;
    const price = servicePrice || Number(newService.price);
    
    if (!name || isNaN(price)) return;

    const { error } = await supabase.from('partner_services').insert([{
      partner_id: selectedPartnerForServices.id,
      name: name,
      price: price
    }]);
    
    if (!error) {
      setNewService({ name: '', price: '' });
      setShowSuggestions(false);
      fetchServices(selectedPartnerForServices.id);
      fetchMasterServices();
    }
  };

  const handleDeleteService = async (id: string) => {
    const { error } = await supabase.from('partner_services').delete().eq('id', id);
    if (!error && selectedPartnerForServices) {
      fetchServices(selectedPartnerForServices.id);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    let saveError: any = null;
    if (editingPartner) {
      const { error } = await supabase.from('partners').update(formData).eq('id', editingPartner.id);
      saveError = error;
      if (error) setErrorMsg('Error al actualizar: ' + error.message);
    } else {
      const { error } = await supabase.from('partners').insert([formData]);
      saveError = error;
      if (error) setErrorMsg('Error al crear: ' + error.message);
    }
    if (!saveError) {
      setIsAdding(false);
      setEditingPartner(null);
      setFormData({
        name: '', slug: '', contact_email: '', logo_url: '', access_password: '', needs_password_reset: false,
        billing_details: { tax_id: '', address: '', postal_code: '', city: '', country: 'España', legal_name: '', po_number: '' }
      });
      fetchPartners();
    }
  };

  const handleEdit = (partner: Partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      slug: partner.slug,
      contact_email: partner.contact_email,
      logo_url: partner.logo_url || '',
      access_password: partner.access_password || '',
      needs_password_reset: partner.needs_password_reset || false,
      billing_details: {
        tax_id: partner.billing_details?.tax_id || '',
        address: partner.billing_details?.address || '',
        postal_code: partner.billing_details?.postal_code || '',
        city: partner.billing_details?.city || '',
        country: partner.billing_details?.country || 'España',
        legal_name: partner.billing_details?.legal_name || '',
        po_number: partner.billing_details?.po_number || ''
      }
    });
    setIsAdding(true);
  };

  const toggleStatus = async (partner: Partner) => {
    const newStatus = partner.status === 'active' ? 'inactive' : 'active';
    await supabase.from('partners').update({ status: newStatus }).eq('id', partner.id);
    fetchPartners();
  };

  const confirmDelete = async (id: string) => {
    setErrorMsg(null);
    const { error } = await supabase.from('partners').delete().eq('id', id);
    if (error) {
      setErrorMsg('No se puede eliminar la marca: ' + error.message);
      setDeletingId(null);
    } else {
      setDeletingId(null);
      fetchPartners();
    }
  };

  const generateDirectLink = (partner: Partner) => {
    const link = `${window.location.origin}/partner/${partner.slug}/portal`;
    navigator.clipboard.writeText(link);
    setCopiedId(partner.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredSuggestions = masterServices.filter(s => 
    s.name.toLowerCase().includes(newService.name.toLowerCase()) && 
    !partnerServices.find(ps => ps.name === s.name)
  ).slice(0, 5);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-brand-black text-2xl">Marcas Partners</h2>
          <p className="font-sans text-brand-gray-500 text-sm mt-1">Configura los datos fiscales y seguridad de los portales.</p>
        </div>
        <button
          onClick={() => { setIsAdding(true); setEditingPartner(null); setErrorMsg(null); }}
          className="bg-brand-black text-white px-5 py-2.5 rounded-full text-sm font-sans flex items-center gap-2 hover:bg-brand-gray-800 transition-colors"
        >
          <i className="ri-add-line" />
          Nueva Marca
        </button>
      </div>

      <AnimatePresence>
        {isAdding && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="bg-brand-cream/30 p-8 rounded-3xl border border-brand-sage/30 shadow-xl">
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {/* Información General */}
                <div className="space-y-6">
                  <h3 className="font-sans text-[11px] uppercase tracking-widest text-brand-gray-400 font-bold border-b border-brand-sage/20 pb-2">Información General</h3>
                  <div className="space-y-4">
                    <div className="space-y-1.5 text-left">
                      <label className="font-sans text-xs text-brand-gray-600 ml-1 font-bold">Nombre Comercial</label>
                      <input type="text" required placeholder="Ej: Burberry" className="w-full bg-white border-none rounded-xl px-4 py-3 text-sm shadow-sm" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value, slug: editingPartner ? formData.slug : e.target.value.toLowerCase().replace(/\s+/g, '-') })} />
                    </div>
                    <div className="space-y-1.5 text-left">
                      <label className="font-sans text-xs text-brand-gray-600 ml-1 font-bold">Email de Contacto</label>
                      <input type="email" required placeholder="partners@brand.com" className="w-full bg-white border-none rounded-xl px-4 py-3 text-sm shadow-sm" value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} />
                    </div>
                    <div className="space-y-1.5 text-left">
                      <label className="font-sans text-xs text-brand-gray-600 ml-1 font-bold">Logo URL</label>
                      <input type="text" placeholder="https://..." className="w-full bg-white border-none rounded-xl px-4 py-3 text-sm shadow-sm" value={formData.logo_url} onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })} />
                    </div>
                  </div>
                </div>

                {/* Seguridad del Portal */}
                <div className="space-y-6 bg-white/50 p-6 rounded-2xl border border-brand-sage/10">
                  <h3 className="font-sans text-[11px] uppercase tracking-widest text-brand-gray-400 font-bold border-b border-brand-sage/20 pb-2">Seguridad del Portal</h3>
                  <div className="space-y-4">
                    <div className="space-y-1.5 text-left">
                      <label className="font-sans text-xs text-brand-gray-600 ml-1 font-bold">Contraseña de Acceso</label>
                      <input type="text" placeholder="Establecer contraseña" className="w-full bg-white border-none rounded-xl px-4 py-3 text-sm shadow-sm font-mono" value={formData.access_password} onChange={(e) => setFormData({ ...formData, access_password: e.target.value })} />
                    </div>
                    <div className="flex items-center gap-3 pt-2">
                      <input 
                        type="checkbox" 
                        id="reset_pwd"
                        className="w-4 h-4 rounded border-brand-gray-200 text-brand-black focus:ring-brand-black"
                        checked={formData.needs_password_reset}
                        onChange={(e) => setFormData({ ...formData, needs_password_reset: e.target.checked })}
                      />
                      <label htmlFor="reset_pwd" className="font-sans text-[11px] text-brand-gray-600 font-bold uppercase tracking-wider cursor-pointer">Obligar a cambiar contraseña</label>
                    </div>
                  </div>
                </div>

                {/* Datos Fiscales */}
                <div className="space-y-6">
                  <h3 className="font-sans text-[11px] uppercase tracking-widest text-brand-gray-400 font-bold border-b border-brand-sage/20 pb-2">Datos Fiscales</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5 text-left">
                        <label className="font-sans text-xs text-brand-gray-600 ml-1 font-bold">Razón Social</label>
                        <input type="text" placeholder="Nombre Legal" className="w-full bg-white border-none rounded-xl px-4 py-3 text-sm shadow-sm" value={formData.billing_details.legal_name} onChange={(e) => setFormData({ ...formData, billing_details: { ...formData.billing_details, legal_name: e.target.value } })} />
                      </div>
                      <div className="space-y-1.5 text-left">
                        <label className="font-sans text-xs text-brand-gray-600 ml-1 font-bold">Código PO (Opcional)</label>
                        <input type="text" placeholder="PO 4701207140" className="w-full bg-white border-none rounded-xl px-4 py-3 text-sm shadow-sm" value={formData.billing_details.po_number} onChange={(e) => setFormData({ ...formData, billing_details: { ...formData.billing_details, po_number: e.target.value } })} />
                      </div>
                    </div>
                    <div className="space-y-1.5 text-left">
                      <label className="font-sans text-xs text-brand-gray-600 ml-1 font-bold">Dirección</label>
                      <input type="text" placeholder="Calle Ejemplo, 123" className="w-full bg-white border-none rounded-xl px-4 py-3 text-sm shadow-sm" value={formData.billing_details.address} onChange={(e) => setFormData({ ...formData, billing_details: { ...formData.billing_details, address: e.target.value } })} />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1.5 text-left">
                        <label className="font-sans text-xs text-brand-gray-600 ml-1 font-bold">NIF / CIF</label>
                        <input type="text" placeholder="A12345678" className="w-full bg-white border-none rounded-xl px-4 py-3 text-sm shadow-sm" value={formData.billing_details.tax_id} onChange={(e) => setFormData({ ...formData, billing_details: { ...formData.billing_details, tax_id: e.target.value } })} />
                      </div>
                      <div className="space-y-1.5 text-left">
                        <label className="font-sans text-xs text-brand-gray-600 ml-1 font-bold">C. Postal</label>
                        <input type="text" placeholder="08001" className="w-full bg-white border-none rounded-xl px-4 py-3 text-sm shadow-sm" value={formData.billing_details.postal_code} onChange={(e) => setFormData({ ...formData, billing_details: { ...formData.billing_details, postal_code: e.target.value } })} />
                      </div>
                      <div className="space-y-1.5 text-left">
                        <label className="font-sans text-xs text-brand-gray-600 ml-1 font-bold">Ciudad</label>
                        <input type="text" placeholder="BCN" className="w-full bg-white border-none rounded-xl px-4 py-3 text-sm shadow-sm" value={formData.billing_details.city} onChange={(e) => setFormData({ ...formData, billing_details: { ...formData.billing_details, city: e.target.value } })} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-brand-sage/20 pt-6">
                <button type="button" onClick={() => setIsAdding(false)} className="px-8 py-3 rounded-full text-[11px] font-sans uppercase tracking-widest text-brand-gray-500 hover:bg-brand-gray-100 transition-colors">Cancelar</button>
                <button type="submit" className="bg-brand-black text-white px-10 py-3 rounded-full text-[11px] font-sans uppercase tracking-widest font-bold hover:bg-brand-gray-800 transition-colors shadow-lg shadow-black/10">{editingPartner ? 'Actualizar Partner' : 'Crear Partner'}</button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="bg-white rounded-[40px] border border-brand-gray-100 overflow-hidden shadow-sm">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-brand-gray-50 border-b border-brand-gray-100">
              <th className="px-8 py-5 font-sans text-[10px] uppercase tracking-[0.2em] text-brand-gray-400 font-bold">Marca / Identidad</th>
              <th className="px-8 py-5 font-sans text-[10px] uppercase tracking-[0.2em] text-brand-gray-400 font-bold">Seguridad</th>
              <th className="px-8 py-5 font-sans text-[10px] uppercase tracking-[0.2em] text-brand-gray-400 font-bold">Estado</th>
              <th className="px-8 py-5 font-sans text-[10px] uppercase tracking-[0.2em] text-brand-gray-400 font-bold text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-gray-50">
            {partners.map((partner) => (
              <tr key={partner.id} className="hover:bg-brand-gray-50/30 transition-colors relative">
                <td className="px-8 py-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-brand-cream flex items-center justify-center overflow-hidden border border-brand-gray-100 shadow-sm">
                      {partner.logo_url ? <img src={partner.logo_url} alt={partner.name} className="w-full h-full object-cover" /> : <i className="ri-building-line text-brand-sage text-xl" />}
                    </div>
                    <div>
                      <p className="font-serif text-brand-black font-medium text-lg italic tracking-tight">{partner.name}</p>
                      <p className="font-sans text-[10px] text-brand-gray-400 uppercase tracking-widest">{partner.contact_email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-8 py-6">
                  <div className="flex items-center gap-2">
                    {partner.access_password ? (
                      <div className="flex flex-col gap-1">
                        <span className="text-[10px] font-mono text-brand-black font-bold uppercase tracking-widest flex items-center gap-1.5">
                          <i className="ri-lock-2-line text-brand-sage" /> {partner.access_password}
                        </span>
                        {partner.needs_password_reset && <span className="text-[8px] text-amber-600 font-bold uppercase tracking-widest">Cambio forzado</span>}
                      </div>
                    ) : (
                      <span className="text-[9px] text-brand-gray-300 italic font-medium uppercase tracking-widest">Sin protección</span>
                    )}
                  </div>
                </td>
                <td className="px-8 py-6">
                  <button onClick={() => toggleStatus(partner)} className={`px-4 py-1.5 rounded-full text-[9px] font-bold uppercase tracking-widest transition-all ${partner.status === 'active' ? 'bg-brand-sage/10 text-brand-sage' : 'bg-brand-gray-100 text-brand-gray-400'}`}>{partner.status === 'active' ? 'Activo' : 'Pausado'}</button>
                </td>
                <td className="px-8 py-6 text-right">
                  <div className="flex items-center justify-end gap-3">
                    <button 
                      onClick={() => generateDirectLink(partner)} 
                      className={`p-2.5 rounded-full transition-all flex items-center gap-2 ${copiedId === partner.id ? 'bg-brand-sage text-white px-4' : 'hover:bg-brand-sage/10 text-brand-gray-400 hover:text-brand-sage'}`}
                      title="Copiar Link"
                    >
                      <i className={copiedId === partner.id ? "ri-check-line" : "ri-share-line"} />
                      {copiedId === partner.id && <span className="text-[9px] uppercase font-bold tracking-widest">¡Copiado!</span>}
                    </button>
                    <button onClick={() => { setSelectedPartnerForServices(partner); fetchServices(partner.id); }} className="p-2.5 hover:bg-brand-sage/10 rounded-full text-brand-gray-400 hover:text-brand-black transition-all" title="Servicios"><i className="ri-price-tag-3-line" /></button>
                    <button onClick={() => handleEdit(partner)} className="p-2.5 hover:bg-brand-sage/10 rounded-full text-brand-gray-400 hover:text-brand-black transition-all" title="Editar"><i className="ri-edit-line" /></button>
                    <button onClick={() => setDeletingId(partner.id)} className="p-2.5 hover:bg-red-50 rounded-full text-brand-gray-400 hover:text-red-600 transition-all" title="Eliminar"><i className="ri-delete-bin-line" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Services Modal */}
      <AnimatePresence>
        {selectedPartnerForServices && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/40 backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white w-full max-w-2xl rounded-[40px] overflow-hidden shadow-2xl border border-brand-gray-100">
              <div className="p-10 border-b border-brand-gray-50 flex items-center justify-between bg-brand-cream/5">
                <div>
                  <h3 className="font-serif text-3xl text-brand-black italic tracking-tighter">Servicios: {selectedPartnerForServices.name}</h3>
                  <p className="font-sans text-[10px] uppercase tracking-[0.2em] text-brand-gray-400 mt-2 font-bold">Atelier × Partner Services</p>
                </div>
                <button onClick={() => setSelectedPartnerForServices(null)} className="p-3 hover:bg-brand-gray-100 rounded-full transition-colors"><i className="ri-close-line text-2xl text-brand-gray-400" /></button>
              </div>

              <div className="p-10 space-y-10 max-h-[60vh] overflow-y-auto text-left">
                <div className="space-y-8">
                  <div className="flex gap-4 items-end bg-brand-gray-50/50 p-8 rounded-3xl border border-brand-gray-100 shadow-inner">
                    <div className="flex-1 space-y-2 relative">
                      <label className="font-sans text-[9px] uppercase tracking-widest text-brand-gray-400 ml-1 font-bold">Nombre del Servicio</label>
                      <input 
                        type="text" placeholder="Ej: Ajuste de Cintura" 
                        className="w-full bg-white border-none rounded-xl px-5 py-3 text-sm shadow-sm" 
                        value={newService.name} 
                        onChange={(e) => { setNewService({ ...newService, name: e.target.value }); setShowSuggestions(true); }}
                        onFocus={() => setShowSuggestions(true)}
                      />
                      
                      <AnimatePresence>
                        {showSuggestions && filteredSuggestions.length > 0 && (
                          <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 5 }} className="absolute z-20 top-full left-0 right-0 mt-3 bg-white border border-brand-gray-100 rounded-[24px] shadow-2xl overflow-hidden">
                            <div className="bg-brand-gray-50 px-5 py-3 border-b border-brand-gray-100 text-left">
                              <p className="text-[9px] uppercase tracking-[0.25em] text-brand-gray-400 font-bold">Sugerencias Atelier</p>
                            </div>
                            {filteredSuggestions.map((s, idx) => (
                              <button 
                                key={idx} 
                                onClick={() => { setNewService({ name: s.name, price: s.price.toString() }); setShowSuggestions(false); }}
                                className="w-full text-left px-5 py-4 hover:bg-brand-sage/10 flex items-center justify-between group transition-all"
                              >
                                <span className="text-sm font-sans text-brand-black font-medium">{s.name}</span>
                                <span className="text-sm font-serif text-brand-sage font-bold italic group-hover:scale-110 transition-transform">{s.price}€</span>
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                    <div className="w-28 space-y-2">
                      <label className="font-sans text-[9px] uppercase tracking-widest text-brand-gray-400 ml-1 font-bold">Precio (€)</label>
                      <input type="number" placeholder="0.00" className="w-full bg-white border-none rounded-xl px-4 py-3 text-sm text-center shadow-sm font-serif font-bold" value={newService.price} onChange={(e) => setNewService({ ...newService, price: e.target.value })} />
                    </div>
                    <button onClick={() => handleAddService()} className="bg-brand-black text-white px-8 py-3 rounded-xl text-[10px] uppercase tracking-widest font-bold hover:bg-brand-gray-800 transition-all h-[46px] shadow-lg shadow-black/10">Añadir</button>
                  </div>
                </div>

                <div className="space-y-4 pt-8 border-t border-brand-gray-50">
                  <h4 className="font-sans text-[10px] uppercase tracking-[0.25em] text-brand-gray-400 font-bold ml-1 text-left">Catálogo Activo</h4>
                  <div className="grid grid-cols-1 gap-3">
                    {partnerServices.map((service) => (
                      <div key={service.id} className="flex items-center justify-between p-5 hover:bg-brand-gray-50/50 transition-all bg-white rounded-2xl border border-brand-gray-50 group">
                        <div className="text-left">
                          <p className="font-sans text-brand-black font-bold text-sm tracking-tight">{service.name}</p>
                          <p className="font-serif text-brand-sage font-bold text-sm italic">{service.price.toFixed(2)}€</p>
                        </div>
                        <button onClick={() => handleDeleteService(service.id)} className="p-2 text-brand-gray-200 hover:text-red-500 hover:bg-red-50 rounded-full transition-all"><i className="ri-delete-bin-line" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
